<template>
  <div class="data-table-condensed data-table-condensed--auto-columns">
    <q-table
      class="data-table-condensed__table scroll"
      title-class="data-table-condensed__table--title"
      dense
      virtual-scroll
      flat
      :columns="columns"
      :pagination.sync="pagination"
      :data="rows"
      no-data-label="No data"
      no-results-label="The filter didn't uncover any results"
      color="primary"
      row-key="name"
      style="height: 400px;"
      :loading="visible"
      ><template v-slot:loading>
        <q-inner-loading showing color="secondary" />
      </template>
      <template v-slot:top-left
        ><strong
          >{{ title }}
          <q-chip color="#f4f5f7" v-if="playerId">{{ playerId }}</q-chip>
          <q-chip color="#f4f5f7" v-else>No Player ID</q-chip>
        </strong>
      </template>
      <template v-slot:top-right="props">
        <q-btn
          color="secondary"
          icon-right="archive"
          label="Download"
          no-caps
          unelevated
          rounded
          :disable="rows.length === 0"
          @click="exportTable"
        />
        <q-btn
          round
          unelevated
          outline
          dense
          class="q-ml-sm"
          color="secondary"
          :icon="props.inFullscreen ? 'fullscreen_exit' : 'fullscreen'"
          @click="props.toggleFullscreen"
        />
      </template>
      <template v-slot:body-cell-Description="props">
        <q-td :props="props" class="auto-width">
          {{ props.row.Description }}
        </q-td>
      </template>
    </q-table>
  </div>
</template>

<script>
import { exportFile } from "quasar";
import { formatCurrency } from "@/utils/formatCurrency";
import { formatDateTime } from "@/utils/formatDate";
import { formatTextLabel } from "@/utils/formatText";

function wrapCsvValue(val, formatFn) {
  let formatted = formatFn !== void 0 ? formatFn(val) : val;
  formatted =
    formatted === void 0 || formatted === null ? "" : String(formatted);
  formatted = formatted.split('"').join('""');
  /**
   * Excel accepts \n and \r in strings, but some other CSV parsers do not
   * Uncomment the next two lines to escape new lines
   */
  // .split('\n').join('\\n')
  // .split('\r').join('\\r')
  return `"${formatted}"`;
}

export default {
  props: {
    title: String,
    dataset: String,
    timer: Number,
    playerId: Number,
  },
  data() {
    return {
      pagination: {
        page: 1,
        rowsPerPage: 10,
      },
      columns: [],
      keys: [],
      rows: [],
      errors: [],
      interval: null,
      dataRefresh: this.$props.timer,
      visible: false,
    };
  },
  computed: {
    numberOfPages() {
      return Math.ceil(this.rows.length / this.pagination.rowsPerPage);
    },
  },
  methods: {
    async fetchData(playerId) {
      // Dataset name is the store procedure name used to return the results.
      const payload = {
        filterOptions: {
          playerId,
          networkId: this.$store.state.userDetails.networkId,
          operatorId: this.$store.state.userDetails.operatorId,
        },
        report: "transactions",
        dataset: this.$props.dataset.replace(/ /g, ""),
      };

      await this.$store
        .dispatch("fetchPlayerData", payload)
        .then((response) => {
          if (response.data[0] !== undefined) {
            const keys = Object.keys(response.data[0]);

            this.columns = keys.map((key) => ({
              name: key,
              label: formatTextLabel(key),
              field: key,
              sortable: true,
              align: "left",
              format: (value) => {
                if (key === "Date" && value !== null) {
                  return formatDateTime(value);
                } else if (
                  (key === "Credit" && value !== null) ||
                  (key === "Debit" && value !== null) ||
                  (key === "Balance After" && value !== null)
                ) {
                  return formatCurrency(value);
                } else if (value !== null) {
                  return `${value}`;
                } else {
                  return `---`;
                }
              },
            }));
          }
          this.rows = response.data;
          this.visible = false;
        })
        .catch((e) => {
          this.errors.push(e);
        });
    },

    exportTable() {
      // naive encoding to csv format
      const content = [this.columns.map((col) => wrapCsvValue(col.label))]
        .concat(
          this.rows.map((row) =>
            this.columns
              .map((col) =>
                wrapCsvValue(
                  typeof col.field === "function"
                    ? col.field(row)
                    : row[col.field === void 0 ? col.name : col.field],
                  col.format
                )
              )
              .join(",")
          )
        )
        .join("\r\n");

      const BOM = "\uFEFF";
      const status = exportFile(
        "table-export.csv",
        BOM + content,
        "text/csv;charset=utf-8"
      );

      if (status !== true) {
        this.$q.notify({
          message: "Browser denied file download...",
          color: "negative",
          icon: "warning",
        });
      }
    },
  },
  watch: {
    playerId: function(id) {
      this.fetchData(id);
      this.visible = true;

      clearInterval(this.interval);
      this.interval = setInterval(() => {
        this.fetchData(id);
      }, 180000);
    },
  },

  beforeDestroy: function() {
    clearInterval(this.interval);
  },
};
</script>
